import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom'
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import ProductContainer from './components/ProductContainer';
import { DragDropContext } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';

import { breadcrumbsActions } from "@store/breadcrumbs";
import { fetchCategory, selectCategoryByCountry } from '@store/store-category-management';
import { getFlashSaleById, getProducts, addProduct, updateProduct, deleteProduct, updateOrder } from '@store/flash-sale-products-management';
import { getProducts as getAllProducts } from '@store/sort-sku-management';
import { showErrorMessage, showSuccessMessage } from '@store/dashboard';
import Dropdown from 'src/components/Dropdown';
import UpdateModal from './components/ProductModal';

const ALL_CATEGORY = 'all';

const columns = [
	{
		id: 'all',
		title: 'All Products',
		products: [],
	},
	{
		id: 'sale',
		title: 'Flash Sale Management',
		products: [],
	},
];

const INIT_PRODUCT_MODAL = {
	product: null,
	existingData: null,
	orderSequence: 0,
};

const OptionContainer = styled.div`
	padding: 16px;
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
`;

const FilterContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const FilterItem = styled.div`
	margin-right: 10px;
	width: 200px;
`;

const DraggableContainer = styled.div`
	display: flex;
	flex-direction: row;
	flex-wrap: nowrap;
	padding: 0 16px;
	justify-content: space-between;
`;


const FilterLabel = styled.div`
	font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
	font-size: 0.9rem;
	font-weight: 500;
	margin: 0 0 5px 0;
`;

const AddProducts = () => {
	const { id } = useParams();
	const dispatch = useDispatch();

	const [isLoading, setIsLoading] = useState(false);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [productList, setProductList] = useState(columns);
	const [country, setCountry] = useState('');
	const [openUpdateModal, setOpenUpdateModal] = useState(false);
	const [productModalData, setProductModalData] = useState(INIT_PRODUCT_MODAL);

	const allProducts = useSelector(state => state.giftSortSku.products);
	const products = useSelector(state => state.flashSaleProductList.flashSaleProducts);
	const flashSale = useSelector(state => state.flashSaleProductList.flashSale);
	const categoryStatus = useSelector((state) => state.giftCategoryList.status);
	const categoryByCountry = useSelector((state) => selectCategoryByCountry(state, country));

	useEffect(() => {
		// update breadcrumbs
		dispatch(
			breadcrumbsActions.setBreadcrumbs([
				{ label: 'Home', url: '/' },
				{ label: 'Flash Sale Management', url: '/flash-sale-management' },
				{ label: id, url: `/flash-sale-management/add-product/${id}` },
			])
		);

		if (id) {
			dispatch(getProducts({ flashSaleId: id, active: true }));
			dispatch(getFlashSaleById({ flashSaleId: id}));
		}
	}, [dispatch, id]);

	useEffect(() => {
		// fetch category data
		dispatch(fetchCategory());
	}, [dispatch]);

	useEffect(() =>  {
		if (flashSale) {
			setCountry(flashSale.country);
		}
	}, [flashSale]);

	useEffect(() => {
		if (categoryByCountry && !selectedCategory) {
			setSelectedCategory(categoryByCountry[0].id);
		}
	}, [categoryByCountry]); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (selectedCategory) {
			dispatch(getAllProducts({ countryCode: country, categoryId: ALL_CATEGORY }));
		}
	}, [dispatch, selectedCategory, country]);


	useEffect(() => {
		const productListState = [...productList];

		const productIds = {};
		for (const product of products) {
			productIds[product.productId] = product;
		}

		productListState[0].products = allProducts.filter(
			product => !productIds[product.id] && (
					selectedCategory === ALL_CATEGORY || 
					product.categories.includes(selectedCategory)
				)
		);
		productListState[1].products = allProducts.filter(
			product => !!productIds[product.id]
		).map(
			product => ({ ...product, flashSaleInfo: productIds[product.id] })
		);
		productListState[1].products.sort((a, b) => a.flashSaleInfo.sortingOrder - b.flashSaleInfo.sortingOrder);
		setProductList([...productListState]);
	}, [products, allProducts]); // eslint-disable-line react-hooks/exhaustive-deps

	const sortOrderInSameList = (payload) => {
		const { source, destination } = payload;
		if (!destination || !source) return null;

		const sourceIndex = source.droppableId;
		const destinationIndex = destination.droppableId;
		if (sourceIndex !== destinationIndex) return null;
		
		const column = productList[sourceIndex];
		const newProducts = [...column.products];
		const removedProduct = newProducts[source.index];
		const newState = [...productList];

		newProducts.splice(source.index, 1);
		newProducts.splice(destination.index, 0, removedProduct);

		newState[sourceIndex] = {
			...column,
			products: [...newProducts],
		};

		return newState;
	}

	const dragInSameList = (payload) => {
		const newState = sortOrderInSameList(payload);
		if (!newState) return;
		setProductList(newState);
	}

	const sortOrderAPI = async (payload) => {
		const result = await dispatch(updateOrder({ 
			flashSaleId: id, 
			payload: { flashSaleProducts: payload }
		}));

		if (updateOrder.fulfilled.match(result)) {
			dispatch(showSuccessMessage('Product update successfully!'));
			await refreshProduct();
		} else {
			console.error('result', result);
			if (result.payload) {
				dispatch(showErrorMessage(`${result.payload.message}: ${result.payload.debugMessage}`));
			} else {
				dispatch(showErrorMessage(`Update product sequence error: ${result.error.message}`));
			}
		}
	}

	const getSortOrderPayload = (productPayload) => (productPayload.map((product, index) => {
		if (!product.flashSaleInfo) return null;
		return {
			id: product.flashSaleInfo.id,
			sortingOrder: index + 1,
		}
	}).filter(e => e));


	const updateProductsSortingOrder = async (payload) => {
		setIsLoading(true);
		const newState = sortOrderInSameList(payload);
		if (!newState) {
			setIsLoading(false);
			return;
		}

		const { source } = payload;
		const sourceIndex = source.droppableId;

		if (
			!newState[sourceIndex].products || 
			newState[sourceIndex].products.length === 0
		) {
			setIsLoading(false);
			return;
		}

		const sortPayload = getSortOrderPayload(newState[sourceIndex].products);

		await sortOrderAPI(sortPayload);
		setIsLoading(false);
		return;
	}

	const dragEndHandler = async (result) => {
		const { source, destination } = result;
		if (!destination || !source) return;
		if (
			source.droppableId === destination.droppableId &&
			source.index === destination.index
		) return;

		const sourceIndex = source.droppableId;
		const destinationIndex = destination.droppableId;

		if (sourceIndex === destinationIndex) {
			// sorting in same list
			if (sourceIndex === '0') {
				// in all product list
				dragInSameList(result);
			} else {
				// drag in falsh sale product list -> update sorting order				
				await updateProductsSortingOrder(result);
			}
		} else {
			if (sourceIndex === '0') {
				// from all product to flash sale -> add flash sale product
				const productSelected = productList[source.droppableId].products[source.index];
				setProductModalData({
					product: productSelected,
					existingData: null,
					orderSequence: productList[1].products.length + 1,
					targetSequence: destination.index + 1,
				});
				setOpenUpdateModal(true);
			} else {
				// from flash sale product list to all product -> delete flash sale product
				setIsLoading(true);
				const productSelected = productList[source.droppableId].products[source.index];
				handleDeleteProduct(productSelected.flashSaleInfo.id);
			}
		}
	};

	const categoryChangeHandler = (e) => {
		if (!e ) return;
		setSelectedCategory(e.id);
	};

	const refreshProduct = async () => {
		setIsLoading(true);
		const result = await dispatch(getProducts({ flashSaleId: id, active: true }));
		if (!getProducts.fulfilled.match(result)) {
			console.error(result.payload ? result.payload : result.error);
			window.location.reload();
		}
		setIsLoading(false);
	}

	const handleAddProduct = async (product, targetSequence) => {
		const result = await dispatch(addProduct({
			flashSaleId: id, 
			payload: product,
		}));

		if (addProduct.fulfilled.match(result)) {
			dispatch(showSuccessMessage('Product added successfully into flash sale!'));
			setOpenUpdateModal(false);

			const sortedPayload = getSortOrderPayload(productList[1].products);
			sortedPayload.splice(
				targetSequence - 1, 
				0, 
				{
					id: result.payload.data.id,
					sortingOrder: targetSequence,
				}
			);

			const payload = sortedPayload.map((p, index) => ({
				id: p.id,
				sortingOrder: index + 1,
			}))
			
			await sortOrderAPI(payload);
			setIsLoading(false);
		} else {
			console.error('result', result);
			if (result.payload) {
				dispatch(showErrorMessage(`${result.payload.message}: ${result.payload.debugMessage}`));
			} else {
				dispatch(showErrorMessage(`Add product error: ${result.error.message}`));
			}
		}
		
		setIsLoading(false);
	}

	const handleUpdateProduct = async (product, flashSaleProductId) => {
		const result = await dispatch(updateProduct({
			flashSaleId: id, 
			id: flashSaleProductId,
			payload: product,
		}));

		if (updateProduct.fulfilled.match(result)) {
			dispatch(showSuccessMessage('Product updated!'));
			setOpenUpdateModal(false);
			await refreshProduct();
		} else {
			console.error('result', result);
			if (result.payload) {
				dispatch(showErrorMessage(`${result.payload.message}: ${result.payload.debugMessage}`));
			} else {
				dispatch(showErrorMessage(`Update product error: ${result.error.message}`));
			}
		}
		
		setIsLoading(false);
	}

	const handleDeleteProduct = async (flashSaleProductId) => {
		const result = await dispatch(deleteProduct({
			flashSaleId: id, 
			id: flashSaleProductId
		}));

		if (deleteProduct.fulfilled.match(result)) {
			dispatch(showSuccessMessage('Product removed successfully!'));
			setOpenUpdateModal(false);
			await refreshProduct();
		} else {
			console.error('result', result);
			if (result.payload) {
				dispatch(showErrorMessage(`${result.payload.message}: ${result.payload.debugMessage}`));
			} else {
				dispatch(showErrorMessage(`Remove product error: ${result.error.message}`));
			}
		}
		
		setIsLoading(false);
	}

	const handleUpdateClick = (product) => {
		setProductModalData({
			product,
			existingData: product.flashSaleInfo,
			orderSequence: product.flashSaleInfo.sortingOrder,
			targetSequence: product.flashSaleInfo.sortingOrder,
		});
		setOpenUpdateModal(true);
	}

	const onModalClose = () => {
		setOpenUpdateModal(false);
	}

	return (
		<ApplicationPageTemplate
			title={'Flash Sale Add Products'}
			description={`Add Flash Sale Products`}
			pageReady={true}
		>
			<OptionContainer>
				<FilterContainer>
					<FilterItem>
						<FilterLabel>Category</FilterLabel>
						<Dropdown
							options={[...categoryByCountry].map((category) => ({
								id: category.id,
								name: category.title,
							}))}
							placeholder="Select Category"
							id="category"
							value={selectedCategory || undefined}
							style={{ width: '100%' }}
							disabled={categoryStatus === 'loading'}
							onSelectChange={categoryChangeHandler}
							variant="outlined"
							background="white"
						/>
					</FilterItem>
				</FilterContainer>
			</OptionContainer>
			{categoryStatus !== 'loading' && (
				<>
					<DraggableContainer>
						<DragDropContext onDragEnd={dragEndHandler}>
							{productList.map((column, index) => (
								<ProductContainer {...column} index={index} key={column.id} onUpdate={handleUpdateClick} />
							))}
						</DragDropContext>
					</DraggableContainer>
				</>
			)}

			<UpdateModal 
				isVisible={openUpdateModal} 
				loadingHandler={setIsLoading} 
				onClose={onModalClose}
				productModalData={productModalData}
				onAddProduct={handleAddProduct}
				onUpdateProduct={handleUpdateProduct}
			/>

			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
				open={isLoading || categoryStatus === 'loading'}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
		</ApplicationPageTemplate>
	);
};

export default AddProducts;
